import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Medal, Calendar, User, Award, MapPin } from "lucide-react";

import { Button } from "../ui/button";
import { apiFetch } from "../../services/api/Awards";
import PageHeader from "../Header";

/* ================= TYPES ================= */

interface AwardItem {
  id: number;
  player: string;
  sport: string;
  year: string;
  district?: string;
  age?: string | null;
  achievement?: string | null;
  image?: string | null;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

interface RangeGroup {
  title: string;
  data: AwardItem[];
  pagination: PaginationMeta | null;
}

/* ================= COMPONENT ================= */

export function RajivGandhiAwardsPage() {
  const { slug } = useParams<{ slug: string }>();

  const [rangeGroups, setRangeGroups] = useState<RangeGroup[]>([]);

  const [awardName, setAwardName] = useState("Awards");

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);

  /* ================= FETCH ================= */

  const fetchAwards = async (pageNum = 1) => {
    if (!slug) return;

    setLoading(true);

    try {
      const res = await apiFetch(`award/${slug}?page=${pageNum}`);

      const responseData = res?.data || {};

      /* AWARD INFO */
      const awardInfo = responseData.award;

      setAwardName(awardInfo?.name || "Awards");

      /* RANGES */
      const ranges = responseData.ranges || {};

      /* CONVERT ALL RANGE DATA */
      const formattedRanges: RangeGroup[] = Object.entries(ranges).map(
        ([rangeTitle, rangeValue]: any) => ({
          title: rangeTitle,

          data:
            rangeValue?.data?.map((item: any) => ({
              id: item.id,
              player: item.player_name,
              sport: item.sport?.name,
              year: item.year,
              district: item.district,
              age: item.age,
              achievement: item.achievement,
              image: item.image_url || item.image || "",
            })) || [],

          pagination: rangeValue?.pagination || null,
        }),
      );

      setRangeGroups(formattedRanges);
    } catch (err) {
      console.error(err);
      setRangeGroups([]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= EFFECT ================= */

  useEffect(() => {
    fetchAwards(page);
  }, [slug, page]);

  /* ================= HELPERS ================= */

  const stripHtml = (html?: string | null) => {
    if (!html) return "";

    return html.replace(/<[^>]*>/g, "").trim();
  };

  const getRangeTitle = (index: number) => {
    const rangeGroup = rangeGroups[index];

    if (rangeGroup.title === "2020-2029") {
      return "2020 - Present";
    }

    if (rangeGroup.title === "0-9") {
      const previousGroup = rangeGroups[index - 1];

      if (previousGroup?.title) {
        const match = previousGroup.title.match(/^(\d{4})-/);

        if (match) {
          return `Before - ${match[1]}`;
        }
      }

      return "Before";
    }

    return rangeGroup.title;
  };

  /* ================= UI ================= */

  return (
    <div className="w-full">
      {/* HEADER */}
      <PageHeader title={awardName} slug={slug} />

      {/* LOADING */}
      {loading && (
        <div className="py-20 text-center text-xl font-semibold text-gray-700">
          Loading awards...
        </div>
      )}

      {/* MAIN */}
      <div className="w-full min-h-screen py-12">
        {/* RANGE LOOP */}
        {!loading &&
          rangeGroups.map((rangeGroup, index) => (
            <div key={rangeGroup.title} className="mb-20">
              {/* RANGE HEADING */}
              <div className="flex items-center gap-4 px-5 mb-10">
                <div className="h-[3px] flex-1 bg-gradient-to-r from-blue-900 to-transparent" />

                <h2 className="text-xl md:text-4xl font-bold text-blue-900 whitespace-nowrap">
                  {getRangeTitle(index)}
                </h2>

                <div className="h-[3px] flex-1 bg-gradient-to-l from-blue-900 to-transparent" />
              </div>

              {/* GRID */}
              <div className="max-w-7xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 px-10 md:px-5 mx-auto">
                {rangeGroup.data.map((award) => (
                  <div
                    key={award.id}
                    className="
                          group
                          relative
                          bg-white
                          rounded-xl
                          overflow-hidden
                          border
                          shadow-md
                          hover:shadow-2xl
                          transition-all
                          duration-300
                        "
                  >
                    {/* IMAGE */}
                    <div className="h-[300px] w-full overflow-hidden bg-gray-100">
                      <img
                        src={award.image || ""}
                        alt={award.player}
                        className="
                              h-full
                              w-full
                              object-cover
                              group-hover:scale-105
                              transition
                              duration-500
                            "
                      />
                    </div>

                    {/* INFO */}
                    <div className="py-5 px-2 text-center">
                      <h3 className="text-2xl font-bold text-blue-900 uppercase whitespace-nowrap">
                        {award.player}
                      </h3>

                      <p className="text-gray-600 text-lg">{award.sport}</p>
                    </div>

                    {/* HOVER */}
                    {/* HOVER INFO - BOTTOM 40% */}
                    <div
                      className="
    absolute
    bottom-0
    left-0
    right-0
    h-[40%]
    bg-blue-900/95
    text-white
    translate-y-full
    group-hover:translate-y-0
    transition-transform
    duration-500
    ease-in-out
    flex
    items-center
    justify-center
    px-4
    z-10
  "
                    >
                      <div className="text-center space-y-2">
                        <h3 className="text-xl md:text-2xl font-bold uppercase whitespace-nowrap">
                          {award.player}
                        </h3>

                        {award.sport && (
                          <p className="flex items-center justify-center gap-2 text-lg">
                            <Medal className="h-5 w-5" />
                            {award.sport}
                          </p>
                        )}

                        {award.year && (
                          <p className="flex items-center justify-center gap-2 text-lg">
                            <Calendar className="h-5 w-5" />
                            Year: {award.year}
                          </p>
                        )}

                        {award.district && (
                          <p className="flex items-center justify-center gap-2 text-lg">
                            <MapPin className="h-5 w-5" />
                            District: {award.district}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* PAGINATION */}
              {rangeGroup.pagination && rangeGroup.pagination.last_page > 1 && (
                <div className="mt-10 px-5">
                  <div
                    className="
                          flex
                          flex-col
                          md:flex-row
                          justify-between
                          items-center
                          gap-5
                          bg-white
                          shadow-xl
                          rounded-3xl
                          p-5
                        "
                  >
                    {/* INFO */}
                    <div
                      className="
                            px-8
                            py-3
                            rounded-full
                            bg-gradient-to-r
                            from-blue-900
                            via-indigo-900
                            to-blue-700
                            text-white
                            font-semibold
                          "
                    >
                      Showing {rangeGroup.pagination.from} -{" "}
                      {rangeGroup.pagination.to} of{" "}
                      {rangeGroup.pagination.total} records
                    </div>

                    {/* BUTTONS */}
                    <div className="flex items-center gap-4">
                      {/* PREV */}
                      <Button
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className="
                              rounded-full
                              px-6
                              py-2
                              bg-gradient-to-r
                              from-blue-900
                              via-indigo-900
                              to-blue-700
                            "
                      >
                        ← Prev
                      </Button>

                      {/* PAGE */}
                      <span
                        className="
                              px-6
                              py-2
                              rounded-full
                              bg-gradient-to-r
                              from-blue-900
                              via-indigo-900
                              to-blue-700
                              text-white
                              font-semibold
                            "
                      >
                        Page {rangeGroup.pagination.current_page} of{" "}
                        {rangeGroup.pagination.last_page}
                      </span>

                      {/* NEXT */}
                      <Button
                        disabled={page === rangeGroup.pagination.last_page}
                        onClick={() => setPage(page + 1)}
                        className="
                              rounded-full
                              px-6
                              py-2
                              bg-gradient-to-r
                              from-blue-900
                              via-indigo-900
                              to-blue-700
                            "
                      >
                        Next →
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

        {/* NO DATA */}
        {!loading && rangeGroups.length === 0 && (
          <div className="py-20 text-center text-gray-600 text-xl">
            No award data available.
          </div>
        )}
      </div>
    </div>
  );
}
